/**
 * Embedded JD panel for JobDetailPage.
 * Two-panel layout: left = JD list, right = selected JD detail (same tabs as JDDetailPage).
 */

import { useState, useRef, useEffect } from 'react';
import {
  FileText, Loader2, RefreshCw, Trash2, Edit2, X, Save,
  Upload, Briefcase, MapPin, DollarSign, Building2, Tag, CheckCircle, Search,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  Button, useAuth, toast, Tabs, TabsList, TabsTrigger, TabsContent,
} from '@aaas/shared';
import { useTranslation } from 'react-i18next';
import {
  useAllJDListQuery, useJDDetailByIdQuery,
  useReparseMutation, useDeleteJDMutation,
  useJDFileUrlQuery, useUpdateJDMutation, useUploadJDFileMutation,
} from '../hooks/useJobDescriptionQueries';
import { PDFViewer } from './PDFViewer';
import { useATSRole } from '../hooks/useATSRole';
import {
  type ParsedJDData,
  type ParsedDataForm,
  emptyForm,
  parsedToForm,
  formToParsed,
  formatFileSize,
  extractFilename,
  isPDF,
} from '../utils/jd-form';
import { ProcessingStatusBadge } from './jd/ProcessingStatusBadge';
import { ParsedSection, TagList, BulletList } from './jd/ParsedDataDisplay';

// ── Main component ─────────────────────────────────────────────────────────

export function JobJDPanel({ jobId }: { jobId: string }) {
  const { currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');

  const [selectedJdId, setSelectedJdId] = useState<string | undefined>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Detail state
  const [activeTab, setActiveTab] = useState<'parsed' | 'preview'>('parsed');
  const [editingParsedData, setEditingParsedData] = useState(false);
  const [parsedDataForm, setParsedDataForm] = useState<ParsedDataForm>(emptyForm);
  const [editingRawContent, setEditingRawContent] = useState(false);
  const [rawContentValue, setRawContentValue] = useState('');
  const [editingFileUrl, setEditingFileUrl] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isEditing = editingParsedData || editingRawContent || editingFileUrl || editingName;

  useEffect(() => {
    if (editingName) nameInputRef.current?.focus();
  }, [editingName]);

  const { data: jdListResponse, isLoading: listLoading } = useAllJDListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jobIds: [jobId],
    q: debouncedQuery || undefined,
    enabled: !!currentWorkspace && !!jobId,
  });

  const jds = jdListResponse?.data?.items ?? [];

  // Auto-select first JD on load
  useEffect(() => {
    if (!selectedJdId && jds.length > 0) setSelectedJdId(jds[0].id);
  }, [jds, selectedJdId]);

  const { data: selectedJd, isLoading: detailLoading } = useJDDetailByIdQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jdId: selectedJdId,
    enabled: !!currentWorkspace && !!selectedJdId,
    pausePolling: isEditing,
  });

  const updateMutation = useUpdateJDMutation({ workspaceId: currentWorkspace?.id ?? '', idToken: idToken ?? '', jobId });
  const reparseMutation = useReparseMutation({ workspaceId: currentWorkspace?.id ?? '', idToken: idToken ?? '', jobId });
  const deleteMutation = useDeleteJDMutation({ workspaceId: currentWorkspace?.id ?? '', idToken: idToken ?? '', jobId });
  const uploadFileMutation = useUploadJDFileMutation({ workspaceId: currentWorkspace?.id ?? '', idToken: idToken ?? '', jobId });

  const fileUrlQuery = useJDFileUrlQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jobId,
    jdId: selectedJdId,
    enabled: activeTab === 'preview' && !!selectedJd?.file_url,
  });

  // Reset detail state when selected JD changes
  useEffect(() => {
    setActiveTab('parsed');
    setEditingName(false);
    setEditingParsedData(false);
    setEditingRawContent(false);
    setEditingFileUrl(false);
    setPendingFile(null);
  }, [selectedJdId]);

  const handleReparse = async () => {
    if (!selectedJdId) return;
    try { await reparseMutation.mutateAsync(selectedJdId); toast.success(t('jdPanel.toast.reparseStarted')); }
    catch { toast.error(t('jdPanel.toast.reparseFailed')); }
  };

  const handleStartNameEdit = () => { setNameValue(selectedJd?.name ?? ''); setEditingName(true); };
  const handleCancelNameEdit = () => { setEditingName(false); setNameValue(''); };
  const handleSaveName = async () => {
    if (!nameValue.trim() || !selectedJdId) return;
    try {
      await updateMutation.mutateAsync({ jdId: selectedJdId, data: { name: nameValue.trim() } });
      setEditingName(false);
      toast.success(t('jdPanel.toast.nameUpdated'));
    } catch { toast.error(t('jdPanel.toast.nameUpdateFailed')); }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedJdId) return;
    try {
      await deleteMutation.mutateAsync(selectedJdId);
      setSelectedJdId(undefined);
      setShowDeleteDialog(false);
      toast.success(t('jdPanel.toast.jdDeleted'));
    } catch { toast.error(t('jdPanel.toast.jdDeleteFailed')); }
  };

  const handleSaveParsedData = async () => {
    if (!selectedJdId) return;
    try {
      await updateMutation.mutateAsync({ jdId: selectedJdId, data: { parsed_data: formToParsed(parsedDataForm) } });
      setEditingParsedData(false);
      toast.success(t('jdPanel.toast.parsedDataUpdated'));
    } catch { toast.error(t('jdPanel.toast.parsedDataFailed')); }
  };

  const handleSaveRawContent = async () => {
    if (!selectedJdId) return;
    try {
      await updateMutation.mutateAsync({ jdId: selectedJdId, data: { raw_content: rawContentValue } });
      setEditingRawContent(false);
      toast.success(t('jdPanel.toast.contentUpdated'));
    } catch { toast.error(t('jdPanel.toast.contentFailed')); }
  };

  const handleSaveNewFile = async () => {
    if (!pendingFile || !selectedJdId) return;
    try {
      await uploadFileMutation.mutateAsync({ jdId: selectedJdId, file: pendingFile });
      setPendingFile(null);
      setEditingFileUrl(false);
      toast.success(t('jdPanel.toast.fileReplaced'));
    } catch { toast.error(t('jdPanel.toast.fileFailed')); }
  };

  const setField = (key: keyof ParsedDataForm, value: string | boolean) =>
    setParsedDataForm(prev => ({ ...prev, [key]: value }));

  const parsed = selectedJd?.parsed_data as ParsedJDData | null;
  const hasParseError = parsed && 'error' in parsed;

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left: JD list ─────────────────────────────────────────────── */}
      <div className="w-56 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
        {/* Search */}
        <div className="p-2 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('jdPanel.searchPlaceholder')}
              className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 placeholder-gray-400"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {listLoading ? (
            <div className="flex justify-center items-center h-20">
              <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />
            </div>
          ) : jds.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-400">
              {debouncedQuery ? t('jdPanel.noResults') : t('jdPanel.noJdsYet')}
            </div>
          ) : jds.map(jd => (
            <button
              key={jd.id}
              onClick={() => setSelectedJdId(jd.id)}
              className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-white transition-colors ${selectedJdId === jd.id ? 'bg-white border-l-2 border-l-orange-500' : ''}`}
            >
              <p className={`text-sm font-medium truncate ${selectedJdId === jd.id ? 'text-orange-600' : 'text-gray-800'}`}>{jd.name}</p>
              <div className="mt-1"><ProcessingStatusBadge status={jd.processing_status} /></div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Right: selected JD detail ─────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {!selectedJdId ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <FileText className="h-12 w-12 mb-3 text-gray-200" />
            <p className="text-sm">{t('jdPanel.selectJd')}</p>
          </div>
        ) : detailLoading && !selectedJd ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="h-6 w-6 text-orange-600 animate-spin" />
          </div>
        ) : selectedJd ? (
          <div className="p-4 space-y-4">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1.5">
                {editingName ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={nameInputRef}
                      value={nameValue}
                      onChange={e => setNameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelNameEdit(); }}
                      className="text-base font-semibold text-gray-900 border-b-2 border-orange-500 focus:outline-none bg-transparent min-w-0 w-48"
                    />
                    <button onClick={handleSaveName} disabled={updateMutation.isPending} className="text-green-600 hover:text-green-700 disabled:opacity-50">
                      {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={handleCancelNameEdit} className="text-gray-400 hover:text-gray-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-base font-semibold text-gray-900">{selectedJd.name}</h2>
                    {isLeaderOrAbove && (
                      <button onClick={handleStartNameEdit} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <ProcessingStatusBadge status={selectedJd.processing_status} />
                  {selectedJd.file_name && (
                    <>
                      <span className="text-xs text-gray-400">{selectedJd.file_name}</span>
                      {selectedJd.file_type && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 uppercase">{selectedJd.file_type}</span>
                      )}
                      {selectedJd.file_size != null && <span className="text-xs text-gray-400">{formatFileSize(selectedJd.file_size)}</span>}
                    </>
                  )}
                </div>
              </div>
              {isLeaderOrAbove && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={handleReparse}
                    disabled={reparseMutation.isPending || selectedJd.processing_status === 'processing'}>
                    {reparseMutation.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                    {t('jdPanel.reparse')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                    <Trash2 className="h-4 w-4 mr-1.5" />{t('jdPanel.delete')}
                  </Button>
                </div>
              )}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'parsed' | 'preview')}>
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="parsed">{t('jdPanel.parsedData')}</TabsTrigger>
                  <TabsTrigger value="preview">{t('jdPanel.previewJd')}</TabsTrigger>
                </TabsList>
                {isLeaderOrAbove && selectedJd.processing_status !== 'processing' && (
                  <>
                    {activeTab === 'parsed' && !editingParsedData && (
                      <button onClick={() => { setParsedDataForm(parsedToForm(parsed)); setEditingParsedData(true); }}
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700">
                        <Edit2 className="h-3 w-3" />{t('jdPanel.edit')}
                      </button>
                    )}
                    {activeTab === 'preview' && !editingFileUrl && !editingRawContent && (
                      selectedJd.file_url ? (
                        <button onClick={() => setEditingFileUrl(true)}
                          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700">
                          <Edit2 className="h-3 w-3" />{t('jdPanel.replaceFile')}
                        </button>
                      ) : selectedJd.raw_content ? (
                        <button onClick={() => { setRawContentValue(selectedJd.raw_content ?? ''); setEditingRawContent(true); }}
                          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700">
                          <Edit2 className="h-3 w-3" />{t('jdPanel.edit')}
                        </button>
                      ) : null
                    )}
                  </>
                )}
              </div>

              {/* ── Parsed Data tab ── */}
              <TabsContent value="parsed" className="mt-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  {selectedJd.processing_status === 'processing' ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3 text-blue-600">
                      <Loader2 className="h-8 w-8 animate-spin" />
                      <p className="text-sm font-medium">{t('jdPanel.aiParsing')}</p>
                    </div>
                  ) : editingParsedData ? (
                    <div className="space-y-5">
                      <div className="space-y-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jdPanel.basicInfo')}</h4>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.titleLabel')}</label>
                          <input value={parsedDataForm.title} onChange={e => setField('title', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.descriptionLabel')}</label>
                          <textarea value={parsedDataForm.description} onChange={e => setField('description', e.target.value)}
                            rows={3} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.employmentType')}</label>
                            <input value={parsedDataForm.employment_type} onChange={e => setField('employment_type', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.experienceLevel')}</label>
                            <input value={parsedDataForm.experience_level} onChange={e => setField('experience_level', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3 border-t border-gray-100 pt-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jdPanel.locationLabel')}</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.cityLabel')}</label>
                            <input value={parsedDataForm.location_city} onChange={e => setField('location_city', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.countryLabel')}</label>
                            <input value={parsedDataForm.location_country} onChange={e => setField('location_country', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.addressLabel')}</label>
                          <input value={parsedDataForm.location_address} onChange={e => setField('location_address', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                          <input type="checkbox" checked={parsedDataForm.location_remote}
                            onChange={e => setField('location_remote', e.target.checked)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                          {t('jdPanel.remotePosition')}
                        </label>
                      </div>
                      <div className="space-y-3 border-t border-gray-100 pt-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jdPanel.salaryLabel')}</h4>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.displayText')}</label>
                          <input value={parsedDataForm.salary_text} onChange={e => setField('salary_text', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {(['salary_min', 'salary_max', 'salary_currency', 'salary_period'] as const).map((k, i) => (
                            <div key={k}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">{[t('jdPanel.min'), t('jdPanel.max'), t('jdPanel.currency'), t('jdPanel.period')][i]}</label>
                              <input value={parsedDataForm[k]} onChange={e => setField(k, e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3 border-t border-gray-100 pt-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('jdPanel.companyLabel')}</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.nameLabel')}</label>
                            <input value={parsedDataForm.company_name} onChange={e => setField('company_name', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.industryLabel')}</label>
                            <input value={parsedDataForm.company_industry} onChange={e => setField('company_industry', e.target.value)}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('jdPanel.descriptionLabel')}</label>
                          <textarea value={parsedDataForm.company_description} onChange={e => setField('company_description', e.target.value)}
                            rows={2} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
                        </div>
                      </div>
                      {([
                        { key: 'responsibilities', label: t('jdPanel.responsibilities'), rows: 6 },
                        { key: 'qualifications', label: t('jdPanel.qualifications'), rows: 6 },
                        { key: 'skills_required', label: t('jdPanel.skillsRequired'), rows: 4 },
                        { key: 'benefits', label: t('jdPanel.benefits'), rows: 4 },
                        { key: 'tags', label: t('jdPanel.tags'), rows: 3 },
                      ] as const).map(({ key, label, rows }) => (
                        <div key={key} className="border-t border-gray-100 pt-4">
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            {label} <span className="font-normal normal-case text-gray-400">{t('jdPanel.onePerLine')}</span>
                          </label>
                          <textarea value={parsedDataForm[key]} onChange={e => setField(key, e.target.value)}
                            rows={rows} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
                        </div>
                      ))}
                      <div className="flex gap-2 border-t border-gray-100 pt-4">
                        <Button size="sm" onClick={handleSaveParsedData} disabled={updateMutation.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                          {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('jdPanel.saveChanges')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingParsedData(false)} disabled={updateMutation.isPending}>{t('jdPanel.cancel')}</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {selectedJd.processing_status === 'pending' && <p className="text-sm text-gray-400">{t('jdPanel.pendingParsing')}</p>}
                      {hasParseError && (
                        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                          <p className="font-medium">{t('jdPanel.parsingFailed')}</p>
                          <p className="mt-1 text-red-600">{parsed?.error?.message ?? t('jdPanel.unknownError')}</p>
                        </div>
                      )}
                      {parsed && !hasParseError && (
                        <div className="space-y-5">
                          {parsed.title && (
                            <div>
                              <h3 className="text-base font-bold text-gray-900">{parsed.title}</h3>
                              {parsed.description && <p className="mt-1 text-sm text-gray-600 leading-relaxed">{parsed.description}</p>}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {parsed.employment_type && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                                <Briefcase className="h-3 w-3" />{parsed.employment_type.replace(/_/g, ' ')}
                              </span>
                            )}
                            {parsed.experience_level && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium">{parsed.experience_level}</span>
                            )}
                            {parsed.location?.remote && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-md text-xs font-medium">{t('jdPanel.remote')}</span>
                            )}
                          </div>
                          {parsed.location && Object.values(parsed.location).some(Boolean) && (
                            <ParsedSection title={t('jdPanel.locationLabel')} icon={<MapPin className="h-4 w-4" />}>
                              <p className="text-sm text-gray-700">{[parsed.location.address, parsed.location.city, parsed.location.country].filter(Boolean).join(', ') || '—'}</p>
                            </ParsedSection>
                          )}
                          {parsed.salary_range && Object.values(parsed.salary_range).some(Boolean) && (
                            <ParsedSection title={t('jdPanel.salaryLabel')} icon={<DollarSign className="h-4 w-4" />}>
                              <p className="text-sm text-gray-700">
                                {parsed.salary_range.text
                                  ? String(parsed.salary_range.text)
                                  : [parsed.salary_range.min_amount, parsed.salary_range.max_amount].filter(v => v != null).join(' – ')
                                    + ` ${parsed.salary_range.currency ?? ''} / ${parsed.salary_range.period ?? ''}`}
                              </p>
                            </ParsedSection>
                          )}
                          {parsed.company_info && Object.values(parsed.company_info).some(Boolean) && (
                            <ParsedSection title={t('jdPanel.companyLabel')} icon={<Building2 className="h-4 w-4" />}>
                              <div className="space-y-1">
                                {parsed.company_info.name && <p className="text-sm font-medium text-gray-800">{parsed.company_info.name}</p>}
                                {parsed.company_info.description && <p className="text-sm text-gray-600">{parsed.company_info.description}</p>}
                                {parsed.company_info.industry && <p className="text-xs text-gray-500">{t('jdPanel.industryLabel')}: {parsed.company_info.industry}</p>}
                              </div>
                            </ParsedSection>
                          )}
                          {parsed.responsibilities?.length ? <ParsedSection title={t('jdPanel.responsibilities')} icon={<CheckCircle className="h-4 w-4" />}><BulletList items={parsed.responsibilities} /></ParsedSection> : null}
                          {parsed.qualifications?.length ? <ParsedSection title={t('jdPanel.qualifications')} icon={<CheckCircle className="h-4 w-4" />}><BulletList items={parsed.qualifications} /></ParsedSection> : null}
                          {parsed.skills_required?.length ? <ParsedSection title={t('jdPanel.skillsRequired')} icon={<Tag className="h-4 w-4" />}><TagList items={parsed.skills_required} /></ParsedSection> : null}
                          {parsed.benefits?.length ? <ParsedSection title={t('jdPanel.benefits')} icon={<CheckCircle className="h-4 w-4" />}><BulletList items={parsed.benefits} /></ParsedSection> : null}
                          {parsed.tags?.length ? <ParsedSection title={t('jdPanel.tags')} icon={<Tag className="h-4 w-4" />}><TagList items={parsed.tags} /></ParsedSection> : null}
                        </div>
                      )}
                      {selectedJd.processing_status === 'processed' && !parsed && <p className="text-sm text-gray-400">{t('jdPanel.noData')}</p>}
                    </>
                  )}
                </div>
              </TabsContent>

              {/* ── Preview JD tab ── */}
              <TabsContent value="preview" className="mt-2">
                {editingFileUrl ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    {selectedJd.file_url && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                        <span>{t('jdPanel.currentFile')} {selectedJd.file_name ?? extractFilename(selectedJd.file_url)}</span>
                        {selectedJd.file_size != null && <span>({formatFileSize(selectedJd.file_size)})</span>}
                      </div>
                    )}
                    {pendingFile ? (
                      <div className="flex items-center gap-2 px-3 py-2.5 border border-green-200 bg-green-50 rounded-md">
                        <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-700 flex-1 truncate">{pendingFile.name}</span>
                        <button onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-green-600 hover:text-green-800">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => fileInputRef.current?.click()}
                        className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300 rounded-md hover:border-orange-400 hover:bg-orange-50 transition-colors">
                        <Upload className="h-7 w-7 text-gray-400" />
                        <span className="text-sm text-gray-500"><span className="text-orange-600 font-medium">{t('jdPanel.clickToSelect')}</span> {t('jdPanel.aNewFile')}</span>
                        <span className="text-xs text-gray-400">{t('jdPanel.acceptedFormats')}</span>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt"
                      onChange={e => setPendingFile(e.target.files?.[0] ?? null)} className="hidden" />
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                      {t('jdPanel.uploadWarning')}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveNewFile} disabled={!pendingFile || uploadFileMutation.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                        {uploadFileMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('jdPanel.saveAndUpdate')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditingFileUrl(false); setPendingFile(null); }} disabled={uploadFileMutation.isPending}>{t('jdPanel.cancel')}</Button>
                    </div>
                  </div>
                ) : selectedJd.file_url ? (
                  <>
                    {fileUrlQuery.isLoading ? (
                      <div className="flex items-center justify-center h-[600px] bg-gray-50 rounded-lg border border-gray-200">
                        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                      </div>
                    ) : fileUrlQuery.isError ? (
                      <div className="flex items-center justify-center h-40 bg-red-50 rounded-lg border border-red-200 text-sm text-red-500">{t('jdPanel.failedToLoadPreview')}</div>
                    ) : fileUrlQuery.data?.url ? (
                      isPDF(selectedJd.file_type ?? selectedJd.file_url) ? (
                        <div className="h-[600px]">
                          <PDFViewer url={fileUrlQuery.data.url} fileName={selectedJd.file_name ?? extractFilename(selectedJd.file_url)} />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-4 py-6 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <a href={fileUrlQuery.data.url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                            {selectedJd.file_name ?? t('jdPanel.openFileNewTab')}
                          </a>
                        </div>
                      )
                    ) : null}
                  </>
                ) : selectedJd.raw_content ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    {editingRawContent ? (
                      <div className="space-y-3">
                        <textarea value={rawContentValue} onChange={e => setRawContentValue(e.target.value)}
                          rows={20} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 font-sans resize-y" />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveRawContent} disabled={updateMutation.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('jdPanel.save')}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingRawContent(false)} disabled={updateMutation.isPending}>{t('jdPanel.cancel')}</Button>
                        </div>
                      </div>
                    ) : (
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{selectedJd.raw_content}</pre>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-400">
                    <FileText className="h-8 w-8 mb-2 text-gray-300" />
                    {t('jdPanel.noFileAttached')}
                    {isLeaderOrAbove && selectedJd.processing_status !== 'processing' && (
                      <button onClick={() => setEditingFileUrl(true)} className="mt-2 text-orange-600 hover:text-orange-700 text-xs">{t('jdPanel.uploadFile')}</button>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </div>

      {/* Delete dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { if (!deleteMutation.isPending) setShowDeleteDialog(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('jdPanel.deleteJdTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('jdPanel.deleteJdDescription', { name: selectedJd?.name })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>{t('jdPanel.cancel')}</AlertDialogCancel>
            <Button onClick={(e) => { e.preventDefault(); handleDeleteConfirm(); }} disabled={deleteMutation.isPending} className="bg-red-600 hover:bg-red-700">
              {deleteMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('jdPanel.deleting')}</> : t('jdPanel.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
